import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Switch,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  Card,
  CardContent,
  InputAdornment,
  Chip,
  Stack,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
  CloudUpload as UploadIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Payments as PaymentsIcon,
  LocalShipping as ShippingIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { supplierService } from '../utils/apiService';
import settingsService from '../services/settingsService';
import { useTranslation } from '../hooks/useTranslation';

const NewSupplier = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    vatRegistrationNumber: '',
    email: '',
    phone: '',
    address: {
      street: '',
      shortAddress: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'SA',
    },
    code: '',
    paymentTerms: '',
    currency: 'SAR',
    isActive: true,
  });

  const [toggles, setToggles] = useState({
    sendWelcomeEmail: true,
    isVatRegistered: true,
    allowBulkEmails: true,
  });

  const [activeTab, setActiveTab] = useState(0);
  const [autoCodeEnabled, setAutoCodeEnabled] = useState(false);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchSettings();
    if (isEditMode) {
      fetchSupplier();
    }
  }, [id]);

  const fetchSettings = async () => {
    try {
      const response = await settingsService.getSettings();
      if (response.success && response.data) {
        const isAutoCode = response.data.general?.supplierAutoCode;
        setAutoCodeEnabled(isAutoCode);
        if (isAutoCode && !isEditMode) {
          try {
            const autoCodeResponse = await settingsService.getAutoCode('supplier');
            if (autoCodeResponse.success && autoCodeResponse.data) {
              setFormData(prev => ({
                ...prev,
                code: autoCodeResponse.data
              }));
            }
          } catch (codeErr) {
            console.error('Error fetching auto code:', codeErr);
            // Fallback to timestamp based if API fails
            setFormData(prev => ({
              ...prev,
              code: `SUPP-${Math.floor(Date.now() / 1000).toString().slice(-6)}`
            }));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const fetchSupplier = async () => {
    try {
      setLoading(true);
      setError(null);
      const supplier = await supplierService.getSupplier(id);
      console.log('Fetched supplier data:', supplier);
      
      setFormData({
        name: supplier.name || '',
        vatRegistrationNumber: supplier.vatRegistrationNumber || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        address: {
          street: supplier.address?.street || '',
          shortAddress: supplier.address?.shortAddress || '',
          city: supplier.address?.city || '',
          state: supplier.address?.state || '',
          postalCode: supplier.address?.postalCode || '',
          country: supplier.address?.country || 'SA',
        },
        code: supplier.code || '',
        paymentTerms: supplier.paymentTerms || '',
        currency: supplier.currency || 'SAR',
        isActive: supplier.isActive !== undefined ? supplier.isActive : true,
      });
    } catch (err) {
      console.error('Error fetching supplier:', err);
      setError(err.message || t('Suppliers.Failed_to_fetch_supplier'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested properties
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleToggleChange = (name) => {
    setToggles({
      ...toggles,
      [name]: !toggles[name],
    });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate required fields
    if (!formData.code.trim()) {
      newErrors.code = t('Suppliers.Supplier_code_required');
    }
    
    if (!formData.name.trim()) {
      newErrors.name = t('Suppliers.Supplier_name_required');
    }
    
    if (!formData.address.street.trim()) {
      newErrors['address.street'] = t('Suppliers.Address_required');
    }
    
    // Validate email format if provided
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('Suppliers.Valid_email_required');
    }
    
    // Validate VAT number format if provided
    if (formData.vatRegistrationNumber && !/^\d{15}$/.test(formData.vatRegistrationNumber)) {
      newErrors.vatRegistrationNumber = t('Suppliers.VAT_number_length');
    }
    
    // Validate phone number format if provided
    if (formData.phone && !/^(\+|00)?[0-9\s-]{6,}$/.test(formData.phone)) {
      newErrors.phone = t('Suppliers.Valid_phone_required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      setError(t('Suppliers.Please_correct_validation_errors'));
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setErrors({});
      
      // Format data for API
      const supplierData = {
        ...formData,
      };
      
      console.log('Submitting supplier data:', supplierData);
      
      if (isEditMode) {
        await supplierService.updateSupplier(id, supplierData);
        console.log('Supplier updated successfully');
      } else {
        await supplierService.createSupplier(supplierData);
        console.log('Supplier created successfully');
      }
      
      navigate('/suppliers');
    } catch (err) {
      console.error('Error saving supplier:', err);
      
      // Handle validation errors from backend
      if (err.validationErrors) {
        setErrors(err.validationErrors);
        
        // Create a summary of validation errors for the alert
        const errorMessages = Object.values(err.validationErrors);
        setError(`${t('Suppliers.Validation_failed')}${errorMessages.join(', ')}`);
      } else {
        setError(err.message || t('Suppliers.Failed_to_save_supplier'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/suppliers');
  };

  // Toggle card component
  const ToggleCard = ({ title, description, name, checked, onChange, infoTooltip }) => (
    <Card variant="outlined" sx={{ mb: 2, border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: '10px' }}>
      <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              {title}
            </Typography>
            {infoTooltip && (
              <Tooltip title={infoTooltip} arrow>
                <IconButton size="small" sx={{ ml: 0.5 }}>
                  <InfoIcon fontSize="small" color="action" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
        <Switch
          checked={checked}
          onChange={() => onChange(name)}
          color="primary"
          sx={{ 
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: '#4361ee',
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: '#4361ee',
            }
          }}
        />
      </CardContent>
    </Card>
  );

  // Update the common styling for all textboxes and dropdowns
  const controlStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
      height: 36,
    },
    '& .MuiInputBase-input': {
      py: 0.75,
      fontSize: '0.875rem'
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem'
    },
    '& .MuiSelect-select': {
      py: 0.75
    }
  };

  // Update the multiline textbox style
  const multilineStyle = {
    ...controlStyle,
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
      minHeight: 36,
    }
  };

  if (loading && isEditMode) {
    return (
      <Container maxWidth={false} sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      <Paper
        elevation={0}
        sx={{
          p: 0.5,
          borderRadius: '4px',
          maxWidth: '1800px',
          margin: '0 auto',
          boxShadow: 'none',
          border: 'none'
        }}
      >
        <form onSubmit={handleSubmit} autoComplete="off">
          <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={handleCancel}
              sx={{ mr: 0.5, bgcolor: 'rgba(67, 97, 238, 0.1)' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
              <Typography variant="h5" component="h1" fontWeight="bold" sx={{ fontSize: '1.25rem' }}>
              {isEditMode ? t('Suppliers.Edit_Supplier') : t('Suppliers.New_Supplier')}
            </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '0.875rem' }}>
              {isEditMode ? t('Suppliers.Update_an_existing_supplier') : t('Suppliers.Add_a_new_supplier_to_your_account')}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mt: 1, 
              mb: 1,
              '& .MuiAlert-message': {
                fontSize: '0.875rem'
              }
            }}
          >
            {error}
          </Alert>
        )}

        {toggles.isVatRegistered && (
            <Alert severity="info" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
            {t('Suppliers.VAT_registered_suppliers_notice')}
          </Alert>
        )}

          <Paper 
            elevation={0} 
            sx={{ 
              p: 0.5, 
              mb: 0.5, 
              borderRadius: '4px',
              boxShadow: 'none',
              border: 'none',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
              },
            }}
          >
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              sx={{
                borderBottom: 1, 
                borderColor: 'divider',
                mb: 0.5,
                minHeight: 36,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: 120,
                  py: 0.5,
                  minHeight: 36,
                  fontSize: '0.875rem'
                }
              }}
            >
              <Tab label={t('Suppliers.Basic_Information')} />
              <Tab label={t('Suppliers.Business_Details')} />
            </Tabs>

            <Grid container spacing={0.5}>
              {/* Basic Information Tab */}
              {activeTab === 0 && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('Suppliers.Supplier_Code')}
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    margin="dense"
                    size="small"
                    sx={controlStyle}
                    autoComplete="off"
                    autoFocus={!autoCodeEnabled}
                    InputProps={{
                      readOnly: autoCodeEnabled || isEditMode,
                    }}
                    error={!!errors.code}
                    helperText={errors.code}
                  />

                  <TextField
                    label={t('Suppliers.Supplier_Name')}
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    margin="dense"
                    size="small"
                    sx={controlStyle}
                    autoComplete="off"
                    error={!!errors.name}
                    helperText={errors.name}
                  />

                  <TextField
                    label={t('Suppliers.Address_Label')}
                    name="address.street"
                    value={formData.address.street}
                    onChange={handleInputChange}
                    fullWidth
                    multiline
                    rows={2}
                    margin="dense"
                    size="small"
                    placeholder={t('Suppliers.Street_Address_Placeholder')}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                          <LocationIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={multilineStyle}
                    autoComplete="off"
                    error={!!errors['address.street']}
                    helperText={errors['address.street']}
                  />

                  <TextField
                    label={t('Suppliers.Short_Address_Label')}
                    name="address.shortAddress"
                    value={formData.address.shortAddress}
                    onChange={handleInputChange}
                    fullWidth
                    margin="dense"
                    size="small"
                    placeholder={t('Suppliers.Short_Address_Placeholder')}
                    sx={controlStyle}
                    autoComplete="off"
                  />

                  <TextField
                    label={t('Suppliers.Email_Address_Label')}
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    fullWidth
                    margin="dense"
                    size="small"
                    placeholder={t('Suppliers.Email_Address_Placeholder')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={controlStyle}
                    autoComplete="off"
                    error={!!errors.email}
                    helperText={errors.email}
                  />

                  <TextField
                    label={t('Suppliers.Phone_Number_Label')}
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    fullWidth
                    margin="dense"
                    size="small"
                    placeholder={t('Suppliers.Phone_Number_Placeholder')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={controlStyle}
                    autoComplete="off"
                    error={!!errors.phone}
                    helperText={errors.phone}
                  />

                  <TextField
                    label={t('Suppliers.Payment_Terms_Label')}
                    name="paymentTerms"
                    value={formData.paymentTerms}
                    onChange={handleInputChange}
                    fullWidth
                    multiline
                    rows={2}
                    margin="dense"
                    size="small"
                    placeholder={t('Suppliers.Payment_Terms_Placeholder')}
                    sx={multilineStyle}
                    autoComplete="off"
                    error={!!errors.paymentTerms}
                    helperText={errors.paymentTerms}
                  />
                </Grid>
              )}

              {/* Business Details Tab */}
              {activeTab === 1 && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('Suppliers.VAT_No_Label')}
                    name="vatRegistrationNumber"
                    value={formData.vatRegistrationNumber}
                    onChange={handleInputChange}
                    fullWidth
                    margin="dense"
                    size="small"
                    sx={controlStyle}
                    autoComplete="off"
                    error={!!errors.vatRegistrationNumber}
                    helperText={errors.vatRegistrationNumber}
                  />

                  <TextField
                    label={t('Suppliers.City_Label')}
                    name="address.city"
                    value={formData.address.city}
                    onChange={handleInputChange}
                    fullWidth
                    margin="dense"
                    size="small"
                    placeholder={t('Suppliers.City_Placeholder')}
                    sx={controlStyle}
                    autoComplete="off"
                  />

                  <TextField
                    label={t('Suppliers.State_Label')}
                    name="address.state"
                    value={formData.address.state}
                    onChange={handleInputChange}
                    fullWidth
                    margin="dense"
                    size="small"
                    placeholder={t('Suppliers.State_Placeholder')}
                    sx={controlStyle}
                    autoComplete="off"
                  />

                  <TextField
                    label={t('Suppliers.Postal_Code_Label')}
                    name="address.postalCode"
                    value={formData.address.postalCode}
                    onChange={handleInputChange}
                    fullWidth
                    margin="dense"
                    size="small"
                    placeholder={t('Suppliers.Postal_Code_Placeholder')}
                    sx={controlStyle}
                    autoComplete="off"
                  />
                </Grid>
              )}
            </Grid>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
          <Button 
            variant="outlined" 
            onClick={handleCancel}
              sx={{ mr: 0.5, fontSize: '0.875rem' }}
              disabled={loading}
          >
            {t('Common.Cancel')}
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            type="submit"
              sx={{ fontSize: '0.875rem' }}
              disabled={loading}
          >
            {loading ? t('Suppliers.Saving') : isEditMode ? t('Suppliers.Update_Supplier_Btn') : t('Suppliers.Save_Supplier_Btn')}
          </Button>
        </Box>
      </form>
      </Paper>
    </Container>
  );
};

export default NewSupplier;